'use client'

import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import ChartDataLabels, { Context as DatalabelsContext } from 'chartjs-plugin-datalabels'
import React, { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'

import useResponsivePadding from '@/lib/useResponsivePadding'

import styles from './Chart.module.css'

// Helper type for dataset properties
export type DatasetType = {
  type: 'bar' | 'line'
  label: string
  data: number[]
  borderColor: string | string[]
  backgroundColor: string | string[]
  borderWidth?: number
  datalabelColor?: string
  border?: boolean
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  ChartDataLabels
)

export const PhnxChart: React.FC<any> = ({
  title,
  variant = 'vertical',
  labels = [],
  yLabels = [],
  datasets = [],
  xScaleLabel = '',
  yScaleLabel = '',
  yScaleType = 'number',
  xScaleType = 'number',
  padding,
  border = false,
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
  const isTablet = typeof window !== 'undefined' && window.innerWidth <= 1250
  const paddingStyles = useResponsivePadding(padding)

  const mobileLabelsPlugin = useMemo(
    () => ({
      id: 'mobileLabels',
      afterDatasetsDraw(chart: any) {
        if (!isMobile || variant !== 'horizontalstacked') return
        const {
          ctx,
          data,
          chartArea: { left },
          scales: { y },
        } = chart

        ctx.save()
        ctx.font = 'bold 14px "proxima-nova", sans-serif'
        ctx.fillStyle = '#1a1a1a'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'bottom'

        data.labels.forEach((label: string, i: number) => {
          const yPos = y.getPixelForValue(i)
          // Offset -40 ensures the title sits perfectly in the wide gap
          ctx.fillText(String(label), left, yPos - 16)
        })
        ctx.restore()
      },
    }),
    [isMobile, variant]
  )

  const finalHeight = useMemo(() => {
    if (variant === 'horizontalstacked') {
      return isMobile ? `${yLabels.length * 130}px` : '500px'
    }
    // Vertical chart heights - reduced overall height
    return isMobile ? '380px' : '550px'
  }, [variant, isMobile, yLabels.length])

  const chartData = useMemo(
    () => ({
      labels: variant === 'vertical' ? labels : variant === 'verticalGraph' ? labels : yLabels,
      datasets: datasets.map((ds: any) => ({
        ...ds,
        // Apply slim bars only to the horizontal view on mobile
        categoryPercentage:
          (variant === 'horizontalstacked' || variant === 'horizontal') && isMobile ? 0.4 : 0.6,
        barPercentage: 1.0,
      })),
    }),
    [labels, yLabels, datasets, variant, isMobile]
  )

  const horizontalStackedOptions: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: isMobile ? 60 : 10,
        right: 25,
        bottom: 10,
      },
    },
    scales: {
      x: {
        stacked: true,
        max: 100,
        ticks: {
          callback: (val) => (xScaleType === 'percent' ? `${val}%` : val),
          color: '#000',
          font: {
            family: 'proxima-nova',
            size: isMobile ? 12 : 18,
          },
        },
      },
      y: {
        stacked: true,
        display: !isMobile,
        grid: { display: false },
        title: {
          display: yScaleLabel !== '',
          text: yScaleLabel,
        },
        ticks: {
          color: '#000',
          font: {
            family: 'proxima-nova',
            size: isMobile ? 12 : 18,
            weight: 600,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: datasets.some((ds: any) => ds.label && ds.label.trim() !== ''),
        position: 'bottom',
        align: 'start',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          font: { family: 'proxima-nova', size: isMobile ? 10 : 14 },
        },
      },
      tooltip: {
        bodyFont: {
          family: 'proxima-nova',
          size: isMobile ? 10 : 12,
        },
        titleFont: {
          family: 'proxima-nova',
          size: isMobile ? 11 : 13,
        },
        callbacks: {
          title: function (context) {
            const label = context[0].label || ''
            const text = String(label).replace(/\|/g, ' ').replace(/,/g, ' ')
            // Split long text into multiple lines on mobile
            if (isMobile && text.length > 25) {
              const words = text.split(' ')
              const lines: string[] = []
              let currentLine = ''

              words.forEach((word) => {
                if ((currentLine + ' ' + word).length > 25) {
                  if (currentLine) lines.push(currentLine)
                  currentLine = word
                } else {
                  currentLine = currentLine ? currentLine + ' ' + word : word
                }
              })
              if (currentLine) lines.push(currentLine)
              return lines
            }
            return text
          },
          label: function (context) {
            const label = (context.dataset.label || '').replace(/,/g, ' ')
            const ds = context.chart.data.datasets
            const val = context.parsed.x ?? 0
            const total = ds.reduce((sum, d) => sum + (d.data[context.dataIndex] as number), 0)
            const percentage = Math.round((val / total) * 100)
            return `${label}: ${percentage}%`
          },
        },
      },
      datalabels: {
        display: true,
        color: (context: DatalabelsContext) => {
          const val = context.dataset.data[context.dataIndex] as number
          // Use black for small values so they're visible if they spill out
          return val < 5 ? '#1a1a1a' : (context.dataset as DatasetType).datalabelColor || '#fff'
        },
        formatter: (val, context) => {
          const ds = context.chart.data.datasets
          const total = ds.reduce((sum, d) => sum + (d.data[context.dataIndex] as number), 0)
          const percentage = Math.round((val / total) * 100)
          return percentage > 0 ? `${percentage}%` : ''
        },
        font: { family: 'proxima-nova', weight: 'bold', size: isMobile ? 11 : 16 },
        anchor: 'center',
        align: 'center',
      },
    },
  }

  const horizontalGraph: ChartOptions<'bar'> = {
    // 1. THIS FLIPS THE CHART
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: datasets.some((ds: any) => ds.label && ds.label.trim() !== ''),
        position: 'bottom' as const,
        align: 'start',
        labels: {
          font: { family: 'proxima-nova' },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          textAlign: 'left',
        },
      },
      tooltip: {
        enabled: true,
        bodyFont: {
          family: 'proxima-nova',
          size: isMobile ? 10 : 12,
        },
        titleFont: {
          family: 'proxima-nova',
          size: isMobile ? 11 : 13,
        },
        callbacks: {
          title: function (context) {
            const label = context[0].label || ''
            const text = String(label).replace(/\|/g, ' ').replace(/,/g, ' ')
            // Split long text into multiple lines on mobile
            if (isMobile && text.length > 25) {
              const words = text.split(' ')
              const lines: string[] = []
              let currentLine = ''

              words.forEach((word) => {
                if ((currentLine + ' ' + word).length > 25) {
                  if (currentLine) lines.push(currentLine)
                  currentLine = word
                } else {
                  currentLine = currentLine ? currentLine + ' ' + word : word
                }
              })
              if (currentLine) lines.push(currentLine)
              return lines
            }
            return text
          },
          label: function (context) {
            const label = (context.dataset.label || '').replace(/,/g, ' ')
            const value = context.parsed.x
            return `${label}: ${value}%`
          },
        },
      },
      datalabels: { display: false },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: yScaleLabel !== '',
          text: yScaleLabel,
        },
        ticks: {
          font: { family: 'proxima-nova' },
          color: '#000',
          callback: function (value: number | string) {
            return yScaleType === 'percent' ? `${value}%` : value
          },
        },
      },
      y: {
        grid: { display: false },
        title: {
          display: xScaleLabel !== '',
          text: xScaleLabel,
        },
        ticks: {
          font: { family: 'proxima-nova' },
          color: '#000',
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
        },
      },
    },
  }

  const verticalGraph: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: datasets.some((ds: any) => ds.label && ds.label.trim() !== ''),
        position: 'bottom' as const,

        labels: {
          font: { family: 'proxima-nova' },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (context) {
            const label = context[0].label || ''
            return String(label).replace(/\|/g, ' ').replace(/,/g, ' ')
          },
          label: function (context) {
            const label = (context.dataset.label || '').replace(/,/g, ' ')
            const value = context.parsed.y
            return `${label}: ${value}%`
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: xScaleLabel !== '',
          text: xScaleLabel,
        },
        ticks: {
          font: { family: 'proxima-nova' },
          color: '#000',
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: yScaleLabel !== '',
          text: yScaleLabel,
        },
        ticks: {
          font: { family: 'proxima-nova' },
          color: '#000',
          callback: function (value: number | string) {
            return yScaleType === 'percent' ? `${value}%` : value
          },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
        },
      },
    },
  }

  const verticalOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30,
        bottom: 10,
      },
    },
    plugins: {
      legend: {
        display: datasets.some((ds: any) => ds.label && ds.label.trim() !== ''),
        position: 'bottom',
        labels: {
          font: { family: 'proxima-nova' },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (context) {
            const label = context[0].label || ''
            return String(label).replace(/\|/g, ' ').replace(/,/g, ' ')
          },
          label: function (context) {
            const label = (context.dataset.label || '').replace(/,/g, ' ')
            const value = context.parsed.y
            return `${label}: ${value}%`
          },
        },
      },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'top',
        offset: 8,
        color: '#444',
        formatter: (val: any) => (yScaleType === 'percent' ? `${val}%` : val),
        font: { family: 'proxima-nova', weight: 'bold', size: isMobile ? 12 : 18 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        title: { display: xScaleLabel !== '', text: xScaleLabel },
        ticks: {
          color: '#000',
          font: {
            family: 'proxima-nova',
            size: isMobile ? 10 : 22,
          },
        },
      },
      y: {
        beginAtZero: true,
        grace: '20%',
        grid: { display: false },
        border: { display: false },
        ticks: { display: false },
        title: {
          display: yScaleLabel !== '',
          text: yScaleLabel,
          font: { family: 'proxima-nova', weight: 'bold' },
        },
      },
    },
  }

  // const chartHeight =
  //   variant === 'horizontalstacked' && isMobile ? `${yLabels.length * 130}px` : '500px'

  return (
    <div
      className={`${styles['chartcontainer']} ${border === true ? styles.chartBorder : ''}`}
      style={{
        ...paddingStyles,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: isMobile ? '100%' : isTablet ? '100%' : '100%',
      }}
    >
      {title && <h3>{title}</h3>}
      <div
        className={styles['chart']}
        style={{
          position: 'relative',
          height: finalHeight,
          width: isMobile ? '100%' : isTablet ? '100%' : border === true ? '100%' : '720px',
          paddingTop: isMobile ? '10px' : '30px',
          paddingBottom: isMobile ? '10px' : '10px',
          paddingLeft: isMobile
            ? '10px'
            : variant === 'vertical' || variant === 'verticalGraph'
              ? '100px'
              : '30px',
          paddingRight: isMobile
            ? '10px'
            : variant === 'vertical' || variant === 'verticalGraph'
              ? '100px'
              : '30px',
          margin: isMobile ? '-20px 0px 0px 0px' : '0px',
        }}
      >
        <Chart
          type="bar"
          data={chartData}
          options={
            variant === 'horizontalstacked'
              ? horizontalStackedOptions
              : variant === 'horizontal'
                ? horizontalGraph
                : variant === 'verticalGraph'
                  ? verticalGraph
                  : verticalOptions
          }
          plugins={[mobileLabelsPlugin]}
        />
      </div>
    </div>
  )
}
